import React, { useCallback, useEffect, useMemo, useState } from 'react';

import requestGetHeroPlannerBanners from '~/queries/customerCommunication/requestGetHeroPlannerBanners';

import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Button,
  Card,
  CardActionArea,
  CardMedia,
  LinearProgress,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';

import { RequestStatus } from '~/consts/requestConstants';

import { isRequestPending, isRequestRejected } from '~/utils/requestUtils';

const emptyValue = {
  id: 'none',
  name: 'None',
  url: '',
};

export function getBannerId(id: string): undefined | string {
  return id === emptyValue.id ? null : id;
}

interface Props {
  name: string;
  label: string;
  initialBannerId?: string;
  brand: string;
}

function ScheduleContentBannerImageInput({ initialBannerId = emptyValue.id, name, label, brand }: Props) {
  const [bannerId, setBannerId] = useState<string | undefined>(initialBannerId);
  const [bannersReq, setBannersReq] = useState<
    Utils.RequestState<Array<CustomerCommunication.HeroPlannerBanner>, string>
  >({
    status: RequestStatus.INITIAL,
  });

  // Make request depend on brand
  const request = useCallback(async () => {
    setBannersReq((currReq) => ({
      status: RequestStatus.PENDING,
      params: undefined,
      result: currReq.result,
    }));

    try {
      const result = await requestGetHeroPlannerBanners(brand);
      setBannersReq({
        status: RequestStatus.FULFILLED,
        params: undefined,
        result,
      });
      // Reset bannerId if the current selection isn’t valid for the new brand
      if (result && !result.some((banner) => banner.id === bannerId)) {
        setBannerId(emptyValue.id);
      }
    } catch (error) {
      setBannersReq({
        status: RequestStatus.REJECTED,
        params: undefined,
        error,
      });
    }
  }, [brand, bannerId]);

  useEffect(() => {
    request();
  }, [request]);

  const bannerOptions = useMemo(() => [emptyValue, ...(bannersReq.result ?? [])], [bannersReq.result]);

  const selectedBanner = useMemo<CustomerCommunication.HeroPlannerBanner>(() => {
    return bannersReq.result?.find((b) => b.id === bannerId);
  }, [bannersReq.result, bannerId]);

  const selectedOption = useMemo(
    () => bannerOptions.find((b) => b.id === bannerId) ?? emptyValue,
    [bannerOptions, bannerId],
  );

  return (
    <Stack direction="column" gap={2}>
      <input type="hidden" name={name} value={bannerId ?? emptyValue.id} />
      <Autocomplete
        options={bannerOptions}
        getOptionLabel={(option) => option.name}
        value={selectedOption}
        onChange={(_, newValue) => setBannerId(newValue?.id ?? emptyValue.id)}
        disabled={isRequestPending(bannersReq)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="standard"
            error={isRequestRejected(bannersReq)}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isRequestPending(bannersReq) && <LinearProgress sx={{ width: 20 }} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
      {isRequestRejected(bannersReq) && (
        <Alert
          severity="error"
          action={
            <Button size="small" color="inherit" onClick={request}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Could not fetch banners.</AlertTitle>
          {bannersReq.error}
        </Alert>
      )}
      <Card elevation={2} sx={{ height: 320 }}>
        {!selectedBanner && <ImageNotSupportedIcon sx={{ m: 1 }} />}
        {!!selectedBanner && (
          <Tooltip placement="top" title="Click to open the image in a new tab" followCursor>
            <CardActionArea sx={{ height: '100%' }} href={selectedBanner.url} target="_blank">
              <CardMedia
                key={selectedBanner.url}
                component="img"
                src={selectedBanner.url}
                alt="banner image preview"
                sx={{ height: '100%', objectFit: 'contain' }}
              />
            </CardActionArea>
          </Tooltip>
        )}
      </Card>
    </Stack>
  );
}
export default ScheduleContentBannerImageInput;
